import { useCallback, useEffect, useRef, useState, type MouseEvent, type PointerEvent } from "react"
import { Input } from "@/components/ui/primitives"
import { useTranslation } from "@/hooks/use-translation"
import { GRIP_DRAG_MIN_HEIGHT_CM, MAX_HEIGHT_CM, MIN_HEIGHT_CM } from "@/lib/scale-sheet-api"
import { commitHeightFromText, heightFromTopRightAnchorDrag } from "@/lib/scale-sheet-height"
import { cn } from "@/lib/utils"

const HIDE_INPUT_DELAY_MS = 250

export const SCALE_SHEET_SELECTION_REST_GRIP = "text-ui-accent/60 opacity-100 group-hover:text-ui-accent"

export const SCALE_SHEET_SELECTION_ACTIVE_GRIP = "text-ui-accent opacity-100"

export const SCALE_SHEET_SELECTION_REST_BORDER =
  "border-ui-accent/60 ring-1 ring-ui-accent/30 hover:border-ui-accent hover:ring-ui-accent/40"

export const SCALE_SHEET_SELECTION_ACTIVE_BORDER = "border-ui-accent ring-ui-accent/40"

type Corner = "topRight" | "topLeft" | "bottomRight" | "bottomLeft"

type Props = {
  heightCm: number
  onHeightChange: (cm: number) => void
  onActiveChange?: (active: boolean) => void
  onDragChange?: (dragging: boolean) => void
  corner?: Corner
  className?: string
  /** Absolute min for typed input; characters default 5cm, props can pass PROP_MIN_HEIGHT_CM. */
  minHeightCm?: number
  /** Grip-drag floor; characters default 50cm, props can pass PROP_GRIP_DRAG_MIN_HEIGHT_CM. */
  minDragHeightCm?: number
  /** Accessible name for grip/input; defaults to character height label. */
  heightLabel?: string
}

type DragState = {
  startHeightCm: number
  startDistPx: number
}

type CornerGripHandleProps = {
  corner: Corner
  ariaLabel: string
  heightCm: number
  minDragHeightCm: number
  highlighted: boolean
  onPointerEnter: () => void
  onPointerLeave: () => void
  onPointerDown: (e: PointerEvent<HTMLDivElement>) => void
  onPointerMove: (e: PointerEvent<HTMLDivElement>) => void
  onPointerUp: (e: PointerEvent<HTMLDivElement>) => void
  onPointerCancel: (e: PointerEvent<HTMLDivElement>) => void
}

type GripDot = { cx: number; cy: number }

const GRIP_VIEWBOX = 16
const GRIP_DOT_RADIUS = 1
const GRIP_DOT_GAP = 2
const GRIP_DOT_STEP = GRIP_DOT_GAP + GRIP_DOT_RADIUS * 2
const GRIP_EDGE_INSET = GRIP_DOT_GAP
const GRIP_CORNER_CX = GRIP_VIEWBOX - GRIP_EDGE_INSET - GRIP_DOT_RADIUS
const GRIP_CORNER_CY = GRIP_EDGE_INSET + GRIP_DOT_RADIUS

/** Top-right resize grip: 6 dots in a diagonal triangular grid, denser toward the corner. */
const GRIP_DOTS: GripDot[] = [
  { cx: GRIP_CORNER_CX, cy: GRIP_CORNER_CY },
  { cx: GRIP_CORNER_CX, cy: GRIP_CORNER_CY + GRIP_DOT_STEP },
  { cx: GRIP_CORNER_CX, cy: GRIP_CORNER_CY + GRIP_DOT_STEP * 2 },
  { cx: GRIP_CORNER_CX - GRIP_DOT_STEP, cy: GRIP_CORNER_CY },
  { cx: GRIP_CORNER_CX - GRIP_DOT_STEP, cy: GRIP_CORNER_CY + GRIP_DOT_STEP },
  { cx: GRIP_CORNER_CX - GRIP_DOT_STEP * 2, cy: GRIP_CORNER_CY },
]

const CORNER_CONFIG: Record<
  Corner,
  {
    anchor: string
    cursor: string
    gripTransform?: string
  }
> = {
  topRight: {
    anchor: "top-0 right-0",
    cursor: "cursor-nesw-resize",
  },
  topLeft: {
    anchor: "top-0 left-0",
    cursor: "cursor-nwse-resize",
    gripTransform: "scale(-1 1) translate(-16 0)",
  },
  bottomRight: {
    anchor: "bottom-0 right-0",
    cursor: "cursor-nwse-resize",
    gripTransform: "scale(1 -1) translate(0 -16)",
  },
  bottomLeft: {
    anchor: "bottom-0 left-0",
    cursor: "cursor-nesw-resize",
    gripTransform: "scale(-1 -1) translate(-16 -16)",
  },
}

function ResizeGripIcon({
  corner,
  highlighted,
  className,
}: {
  corner: Corner
  highlighted: boolean
  className?: string
}) {
  const config = CORNER_CONFIG[corner]

  return (
    <svg
      aria-hidden
      viewBox={`0 0 ${GRIP_VIEWBOX} ${GRIP_VIEWBOX}`}
      className={cn(
        "pointer-events-none h-full w-full transition-[color,opacity] duration-150",
        highlighted ? SCALE_SHEET_SELECTION_ACTIVE_GRIP : SCALE_SHEET_SELECTION_REST_GRIP,
        className,
      )}
    >
      <g transform={config.gripTransform} fill="currentColor" pointerEvents="none">
        {GRIP_DOTS.map((dot, index) => (
          <circle key={index} cx={dot.cx} cy={dot.cy} r={GRIP_DOT_RADIUS} pointerEvents="none" />
        ))}
      </g>
    </svg>
  )
}

function CornerGripHandle({
  corner,
  ariaLabel,
  heightCm,
  minDragHeightCm,
  highlighted,
  onPointerEnter,
  onPointerLeave,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
}: CornerGripHandleProps) {
  const config = CORNER_CONFIG[corner]

  return (
    <div
      role="slider"
      aria-label={ariaLabel}
      aria-valuemin={minDragHeightCm}
      aria-valuemax={MAX_HEIGHT_CM}
      aria-valuenow={heightCm}
      className={cn("pointer-events-auto relative h-4 w-4 shrink-0 touch-none", config.cursor)}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
    >
      <ResizeGripIcon corner={corner} highlighted={highlighted} />
    </div>
  )
}

function poseAnchorDistance(clientX: number, clientY: number, poseRect: DOMRect) {
  return Math.hypot(clientX - poseRect.left, clientY - poseRect.bottom)
}

export function ScaleSheetHeightResizeHandle({
  heightCm,
  onHeightChange,
  onActiveChange,
  onDragChange,
  corner = "topRight",
  className,
  minHeightCm = MIN_HEIGHT_CM,
  minDragHeightCm = GRIP_DRAG_MIN_HEIGHT_CM,
  heightLabel,
}: Props) {
  const { t } = useTranslation(["workspace"])
  const resolvedHeightLabel = heightLabel ?? t("charactersPage.scaleSheetHeightLabel")
  const [textValue, setTextValue] = useState(String(heightCm))
  const [handleHovered, setHandleHovered] = useState(false)
  const [inputHovered, setInputHovered] = useState(false)
  const [inputFocused, setInputFocused] = useState(false)
  const [dragging, setDragging] = useState(false)
  const dragRef = useRef<DragState | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const hideTimerRef = useRef<number | null>(null)
  const showHeightInput = handleHovered || inputHovered || inputFocused || dragging
  const resizeActive = handleHovered || dragging
  const minCmRef = useRef(minDragHeightCm)
  minCmRef.current = minDragHeightCm
  const minHeightCmRef = useRef(minHeightCm)
  minHeightCmRef.current = minHeightCm

  useEffect(() => {
    setTextValue(String(heightCm))
  }, [heightCm])

  useEffect(() => {
    onActiveChange?.(resizeActive)
  }, [onActiveChange, resizeActive])

  useEffect(() => {
    onDragChange?.(dragging)
  }, [onDragChange, dragging])

  useEffect(() => {
    if (!dragging) return

    document.body.style.userSelect = "none"
    document.body.style.cursor = "grabbing"

    return () => {
      document.body.style.userSelect = ""
      document.body.style.cursor = ""
    }
  }, [dragging])

  useEffect(() => {
    return () => {
      if (hideTimerRef.current != null) {
        window.clearTimeout(hideTimerRef.current)
      }
    }
  }, [])

  const cancelHide = useCallback(() => {
    if (hideTimerRef.current != null) {
      window.clearTimeout(hideTimerRef.current)
      hideTimerRef.current = null
    }
  }, [])

  const scheduleHide = useCallback(() => {
    cancelHide()
    hideTimerRef.current = window.setTimeout(() => {
      setHandleHovered(false)
      setInputHovered(false)
      hideTimerRef.current = null
    }, HIDE_INPUT_DELAY_MS)
  }, [cancelHide])

  const onHandleEnter = useCallback(() => {
    cancelHide()
    setHandleHovered(true)
  }, [cancelHide])

  const onHandleLeave = useCallback(() => {
    scheduleHide()
  }, [scheduleHide])

  const onInputEnter = useCallback(() => {
    cancelHide()
    setInputHovered(true)
  }, [cancelHide])

  const onInputLeave = useCallback(() => {
    scheduleHide()
  }, [scheduleHide])

  const commitText = (raw: string) => {
    const result = commitHeightFromText(raw, heightCm, minHeightCmRef.current)
    if (!result.ok) {
      setTextValue(String(result.revertTo))
      return
    }
    setTextValue(String(result.value))
    onHeightChange(result.value)
  }

  const stopBubble = (e: MouseEvent | PointerEvent) => {
    e.stopPropagation()
  }

  const poseRect = () => containerRef.current?.parentElement?.getBoundingClientRect()

  const handlePointerDown = (e: PointerEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()

    const bbox = poseRect()
    if (!bbox || bbox.width <= 0 || bbox.height <= 0) return

    const startDistPx = poseAnchorDistance(e.clientX, e.clientY, bbox)
    if (startDistPx <= 0) return

    dragRef.current = {
      startHeightCm: heightCm,
      startDistPx,
    }

    cancelHide()
    setHandleHovered(true)
    setDragging(true)
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  const handlePointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    if (!drag) return

    const bbox = poseRect()
    if (!bbox) return

    const currentDistPx = poseAnchorDistance(e.clientX, e.clientY, bbox)
    const nextHeight = heightFromTopRightAnchorDrag(
      drag.startHeightCm,
      drag.startDistPx,
      currentDistPx,
      minCmRef.current,
    )

    onHeightChange(nextHeight)
  }

  const endDrag = (e: PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return

    dragRef.current = null
    setDragging(false)

    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId)
    }
  }

  const anchorClass = CORNER_CONFIG[corner].anchor
  const isTopCorner = corner === "topRight" || corner === "topLeft"

  return (
    <div
      ref={containerRef}
      className={cn("pointer-events-none absolute z-20 touch-none", anchorClass, className)}
      onClick={stopBubble}
      onPointerDown={stopBubble}
    >
      <CornerGripHandle
        corner={corner}
        ariaLabel={resolvedHeightLabel}
        heightCm={heightCm}
        minDragHeightCm={minDragHeightCm}
        highlighted={handleHovered || dragging}
        onPointerEnter={onHandleEnter}
        onPointerLeave={onHandleLeave}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      />
      <div
        className={cn(
          "absolute transition-opacity duration-150 ease-out",
          isTopCorner ? "top-full mt-1" : "bottom-full mb-1",
          // Keep field visible when figure is narrower than the input (overflows past left/right of grip).
          corner === "topRight" || corner === "bottomRight" ? "right-0" : "left-0",
          showHeightInput ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0",
        )}
        onPointerEnter={onInputEnter}
        onPointerLeave={onInputLeave}
      >
        <Input
          type="number"
          min={minHeightCm}
          max={MAX_HEIGHT_CM}
          step={1}
          value={textValue}
          onChange={(e) => setTextValue(e.target.value)}
          onFocus={() => {
            cancelHide()
            setInputFocused(true)
          }}
          onBlur={() => {
            setInputFocused(false)
            commitText(textValue)
            scheduleHide()
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault()
              commitText(textValue)
            }
          }}
          className="h-6 w-12 [appearance:textfield] border-ui-border bg-ui-surface/95 px-1.5 text-center text-[10px] tabular-nums shadow-sm backdrop-blur-sm [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          aria-label={resolvedHeightLabel}
          tabIndex={showHeightInput ? 0 : -1}
        />
      </div>
    </div>
  )
}
